import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import message from '../../utils/message';
import styles from './asset/userBillingHolder.module.scss';
import CancelSubscriptionModal from './CancelSubscriptionModal/CancelSubscriptionModal';
import BugReportModal from './BugReportModal/BugReportModal';
import SupportRequestModal from './SupportRequestModal/SupportRequestModal';
import RefundFormModal from './RefundFormModal/RefundFormModal';
import { getPackageById } from '../../reduxStore/actions/packageActionsHelper';
import { usersData$ } from '../../reduxStore/selectors/userSelectors';
import { selectedPackage$, packageLoading$ } from '../../reduxStore/selectors/packageSelectors';

const UserBillingHolder = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const userData = useSelector(usersData$);
    const packageData = useSelector(selectedPackage$);
    const packageLoading = useSelector(packageLoading$);
    const [isCanceled, setIsCanceled] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [bugReportModalVisible, setBugReportModalVisible] = useState(false);
    const [supportModalVisible, setSupportModalVisible] = useState(false);
    const [refundModalVisible, setRefundModalVisible] = useState(false);

    // Check if subscription is active based on end date
    const isSubscriptionActive = () => {
        if (!userData?.subscription_end_date) {
            return false;
        }

        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0); // Trim to midnight

        const endDate = new Date(userData.subscription_end_date);
        endDate.setHours(0, 0, 0, 0); // Trim to midnight

        return endDate >= currentDate;
    };

    const subscriptionActive = isSubscriptionActive();

    // Fetch package data when user's selected_package changes
    useEffect(() => {
        if (userData?.selected_package) {
            dispatch(getPackageById.request(userData.selected_package));
        }
    }, [dispatch, userData?.selected_package]);

    // Calculate start and end dates
    const getSubscriptionDates = () => {
        if (userData?.subscription_end_date) {
            const endDate = new Date(userData.subscription_end_date);
            return { endDate };
        }
        // Default fallback
        const startDate = new Date();
        const endDate = new Date(startDate);
        endDate.setMonth(endDate.getMonth() + 3);
        return { endDate };
    };

    const { endDate } = getSubscriptionDates();

    const formatDate = (date) => {
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const year = date.getFullYear().toString().slice(-2);
        return `${month}/${day}/${year}`;
    };

    const handleCancelClick = () => {
        setModalVisible(true);
    };

    const handleConfirmCancel = ({ reason, feedback }) => {
        console.log('Cancellation reason:', reason);
        console.log('Feedback:', feedback);
        setIsCanceled(true);
        setModalVisible(false);
        message.success('Subscription canceled successfully. Access continues until end of billing period.');
    };

    const handleUpgradeClick = () => {
        navigate('/user-package');
    };

    // Calculate days remaining in subscription
    const getDaysRemaining = () => {
        if (!userData?.subscription_end_date) {
            return 0;
        }
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const endDate = new Date(userData.subscription_end_date);
        endDate.setHours(0, 0, 0, 0);
        const diffTime = endDate - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return Math.max(0, diffDays);
    };

    // Format price with thousand separators
    const formatPrice = (price) => {
        if (price === null || price === undefined) return '0';
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        }).format(price);
    };

    // Manual refresh function
    const handleRefreshData = () => {
        if (userData?.id) {
            if (userData.selected_package) {
                dispatch(getPackageById.request(userData.selected_package));
            }
            message.success('Package data refreshed successfully');
        }
    };

    return (
        <div className={styles.frameParent}>
            <div className={styles.manageBillingParent}>
                <div className={styles.manageBilling}>Manage Subscription</div>
                <button className={styles.refreshBtn} onClick={handleRefreshData} title="Refresh subscription data">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0115.5-8.5l4 4M22 12.5a10 10 0 01-15.5 8.5l-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span>Refresh</span>
                </button>
            </div>
            <div className={styles.frameGroup}>
                <div className={styles.cardBillingParent}>
                    <div className={styles.cardBilling}>
                        <div className={styles.planHeader}>
                            <div className={styles.planTitle}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z" stroke="#1890ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                                </svg>
                                <span className={styles.planTitleText}>Current Plan</span>
                            </div>
                            <div className={`${styles.statusBadge} ${!subscriptionActive || isCanceled ? styles.statusInactive : styles.statusActive}`}>
                                {!subscriptionActive ? 'Expired' : (isCanceled ? 'Canceled' : 'Active')}
                            </div>
                        </div>

                        <div className={styles.planContent}>
                            {packageLoading ? (
                                <div className={styles.planLoading}>
                                    <div className={styles.spinner}></div>
                                    <p>Loading plan details...</p>
                                </div>
                            ) : packageData ? (
                                <>
                                    <div className={styles.planName}>{packageData.name || 'N/A'}</div>
                                    {packageData.price_vnd !== undefined && packageData.price_vnd !== null && (
                                        <div className={styles.planPrice}>
                                            <span className={styles.priceAmount}>{formatPrice(packageData.price_vnd)} VND{packageData.price_usd !== undefined ? ` · $${packageData.price_usd} USD` : ''}</span>
                                            <span className={styles.pricePeriod}>/{packageData.duration_type || 'month'}</span>
                                        </div>
                                    )}
                                    {packageData.features && packageData.features.length > 0 && (
                                        <div className={styles.planFeatures}>
                                            {packageData.features.slice(0, 3).map((feature, index) => (
                                                <div key={index} className={styles.featureItem}>
                                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="#52c41a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                    <span>{feature}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className={styles.noPlan}>
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="12" cy="12" r="10" stroke="#d9d9d9" strokeWidth="2" />
                                        <path d="M12 8v4M12 16h.01" stroke="#d9d9d9" strokeWidth="2" strokeLinecap="round" />
                                    </svg>
                                    <p>No Active Plan</p>
                                    <span>Subscribe to unlock all features</span>
                                </div>
                            )}
                        </div>

                        <div className={styles.planActions}>
                            <button className={styles.upgradeBtn} onClick={handleUpgradeClick}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 19V5M5 12l7-7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <span>
                                    {!subscriptionActive ? 'Renew Plan' : (packageData ? 'Change Plan' : 'Choose Plan')}
                                </span>
                            </button>
                            {subscriptionActive && !isCanceled && packageData && (
                                <button className={styles.cancelBtn} onClick={handleCancelClick}>
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                    </svg>
                                    <span>Cancel Plan</span>
                                </button>
                            )}
                        </div>
                    </div>

                    <div className={styles.subscriptionInfoCard}>
                        <div className={styles.infoHeader}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="12" cy="12" r="10" stroke="#7d7fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M12 6v6l4 2" stroke="#7d7fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            <span className={styles.infoTitle}>Subscription Details</span>
                        </div>

                        <div className={styles.infoContent}>
                            {subscriptionActive && userData?.subscription_end_date ? (
                                <>
                                    <div className={styles.infoItem}>
                                        <div className={styles.infoLabel}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <rect x="3" y="4" width="18" height="18" rx="2" stroke="#777e90" strokeWidth="2" />
                                                <path d="M16 2v4M8 2v4M3 10h18" stroke="#777e90" strokeWidth="2" strokeLinecap="round" />
                                            </svg>
                                            <span>Days Remaining</span>
                                        </div>
                                        <div className={styles.infoValue}>
                                            <span className={styles.daysCount}>{getDaysRemaining()}</span>
                                            <span className={styles.daysText}>days</span>
                                        </div>
                                    </div>

                                    <div className={styles.infoDivider}></div>

                                    <div className={styles.infoItem}>
                                        <div className={styles.infoLabel}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <circle cx="12" cy="12" r="10" stroke="#777e90" strokeWidth="2" />
                                                <path d="M12 6v6l4 2" stroke="#777e90" strokeWidth="2" strokeLinecap="round" />
                                            </svg>
                                            <span>Expires On</span>
                                        </div>
                                        <div className={styles.infoValue}>
                                            <span className={styles.dateText}>{formatDate(endDate)}</span>
                                        </div>
                                    </div>

                                    <div className={styles.infoDivider}></div>

                                    <div className={styles.infoItem}>
                                        <div className={styles.infoLabel}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="#777e90" strokeWidth="2" />
                                                <path d="M9 12l2 2 4-4" stroke="#777e90" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                            <span>Auto Renewal</span>
                                        </div>
                                        <div className={styles.infoValue}>
                                            <span className={styles.renewalStatus}>
                                                {isCanceled ? 'Disabled' : 'Active'}
                                            </span>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className={styles.noSubscriptionInfo}>
                                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z" stroke="#d9d9d9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M9 12l2 2 4-4" stroke="#d9d9d9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    <p>No Active Subscription</p>
                                    <span>Choose a plan to get started</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Support Actions */}
                <div className={styles.supportActions}>
                    <button className={styles.supportBtn} onClick={() => setBugReportModalVisible(true)}>
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9 1.5C4.85786 1.5 1.5 4.85786 1.5 9C1.5 13.1421 4.85786 16.5 9 16.5C13.1421 16.5 16.5 13.1421 16.5 9C16.5 4.85786 13.1421 1.5 9 1.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M6.75 6.75C6.75 5.50736 7.75736 4.5 9 4.5C10.2426 4.5 11.25 5.50736 11.25 6.75C11.25 7.99264 10.2426 9 9 9V10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            <circle cx="9" cy="13.5" r="0.75" fill="currentColor" />
                        </svg>
                        <span>Report Bug</span>
                    </button>

                    <button className={styles.supportBtn} onClick={() => setSupportModalVisible(true)}>
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9 16.5C13.1421 16.5 16.5 13.1421 16.5 9C16.5 4.85786 13.1421 1.5 9 1.5C4.85786 1.5 1.5 4.85786 1.5 9C1.5 10.05 1.7325 11.0475 2.145 11.9475L1.5 16.5L6.0525 15.855C6.9525 16.2675 7.95 16.5 9 16.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span>Request Support</span>
                    </button>

                    <button className={styles.supportBtn} onClick={() => setRefundModalVisible(true)}>
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M6 7.5H12M6 10.5H9.75M3 13.5V4.5C3 3.67157 3.67157 3 4.5 3H13.5C14.3284 3 15 3.67157 15 4.5V10.5C15 11.3284 14.3284 12 13.5 12H6L3 15V13.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span>Request Refund</span>
                    </button>
                </div>
            </div>

            <CancelSubscriptionModal
                visible={modalVisible}
                onClose={() => setModalVisible(false)}
                onConfirm={handleConfirmCancel}
            />

            <BugReportModal
                visible={bugReportModalVisible}
                onClose={() => setBugReportModalVisible(false)}
            />

            <SupportRequestModal
                visible={supportModalVisible}
                onClose={() => setSupportModalVisible(false)}
            />

            <RefundFormModal
                visible={refundModalVisible}
                onClose={() => setRefundModalVisible(false)}
            />
        </div>
    );
};

export default UserBillingHolder;